
import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NotificationBell from "./NotificationBell";
import {
  MessageSquare,
  MessageCircle,
  Users,
  UserPlus,
  Send,
  Settings,
  Bot,
  Phone,
  Menu,
  X,
  LogOut,
  BarChart3,
  Headphones,
  Moon,
  Sun,
  Shield,
  Building,
  Smartphone,
  Zap,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Wallet,
  DollarSign,
  FileText,
  Server,
  Radar,
  Camera,
  Megaphone,
  FolderOpen,
  LayoutDashboard,
  TrendingUp,
  Hash,
  Eye,
  BarChart2,
  Lightbulb,
  Sparkles,
  ShoppingCart,
  Mail,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useInstances } from "../contexts/InstancesContext";
import api, { integracoesAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import UserBadge from "./UserBadge";

const Layout = ({ children, fullWidth = false, expanded = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [temIntegracao, setTemIntegracao] = useState(false);
  // Capabilities ativas do cliente (ex. ['audiencia'], ['catalogo','pedidos']).
  // O menu depende da funcionalidade, não de "existe integração": antes uma
  // integração de audiência (Mautic) fazia aparecer Pedidos e Produtos, que
  // são telas de e-commerce do Bling.
  const [capabilities, setCapabilities] = useState([]);
  const temCap = (c) => capabilities.includes(c);
  const [openMenus, setOpenMenus] = useState({});

  const { darkMode, setDarkMode } = useTheme();
  const { hasZApiInstance, hasMetaInstance, hasInstagramInstance, poolHabilitado, loading: instancesLoading } = useInstances();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('sidebarCollapsed', String(next));
  };
  
  // Usar try-catch para evitar erro se AuthContext não estiver disponível
  let user = null;
  let isAdmin = () => false;
  let isGerente = () => false;
  let isUsuario = () => false;
  let logout = () => {};
  
  try {
    const auth = useAuth();
    user = auth.user;
    isAdmin = auth.isAdmin;
    isGerente = auth.isGerente;
    isUsuario = auth.isUsuario;
    logout = auth.logout;
  } catch (error) {
    // AuthContext não disponível, usar valores padrão
    user = JSON.parse(localStorage.getItem('user') || '{}');
  }

  const isSuspensa = user?.conta_suspensa === true;

  const fullNavigation = [
    { name: "Dashboard", href: "/", icon: LayoutDashboard },
    { name: "Atendimento", icon: Headphones, children: [
      ...(isUsuario() ? [{ name: "Conversas", href: "/conversas", icon: MessageSquare }] : []),
      { name: "Contatos", href: "/contatos", icon: Users },
      { name: "Segmentação", href: "/segmentos", icon: FolderOpen },
      ...(isUsuario() ? [{ name: "Agente de IA", href: "/atendimento/agente-ia", icon: Bot }] : []),
      { name: "Grupos WhatsApp", href: "/grupos-wa", icon: Users },
    ]},
    ...(isUsuario() ? [{ name: "Marketing", icon: Megaphone, children: [
      { name: "Campanhas", href: "/marketing/campanhas", icon: Send },
      ...(hasMetaInstance ? [{ name: "Templates", href: "/marketing/templates", icon: FileText }] : []),
      // Personas exigem número Meta E o pool habilitado para o cliente.
      ...(hasMetaInstance && poolHabilitado ? [{ name: "Personas", href: "/marketing/personas", icon: Users }] : []),
      ...(hasInstagramInstance ? [{ name: "Publicações", href: "/marketing/publicacoes", icon: Camera }] : []),
      // Cobertura alimenta as campanhas de remarketing, por isso fica em Marketing
      ...(temCap('audiencia') ? [{ name: "Cobertura", href: "/cobertura", icon: Radar }] : []),
    ]}] : []),
    ...(hasInstagramInstance && !instancesLoading ? [{ name: "Insights", icon: Lightbulb, children: [
      { name: "Concorrência", href: "/insights/inteligencia", icon: Eye },
      { name: "Gerador de Ideias", href: "/insights/ideias", icon: Sparkles },
      { name: "Hashtags", href: "/insights/hashtags", icon: Hash },
    ]}] : []),
    ...(temCap('pedidos') ? [{ name: "Pedidos", href: "/pedidos", icon: ShoppingCart }] : []),
    { name: "Analytics", href: "/analytics", icon: BarChart3 },
    ...(isGerente() ? [{ name: "Financeiro", href: "/financeiro", icon: Wallet }] : []),
    ...(isUsuario() ? [{ name: "Configurações", icon: Settings, children: [
      { name: "Canais", href: "/configuracoes/canais", icon: Smartphone },
      ...(temCap('catalogo') ? [{ name: "Produtos", href: "/configuracoes/produtos", icon: ShoppingCart }] : []),
      ...(isAdmin() || isGerente() ? [{ name: "Usuários", href: "/configuracoes/usuarios", icon: Shield }] : []),
    ]}] : []),
  ];

  const navigation = isSuspensa
    ? fullNavigation.filter(i => ['Dashboard', 'Financeiro'].includes(i.name))
    : fullNavigation;

  const adminMenuItems = [
    { name: "Usuários", href: "/admin/users", icon: Users },
    { name: "Clientes", href: "/admin/clients", icon: Building },
    { name: "Leads (formulário)", href: "/admin/leads-contato", icon: Mail },
    { name: "Leads (cadastros)", href: "/admin/leads", icon: TrendingUp },
    { name: "Vendedores", href: "/admin/vendedores", icon: Users },
    { name: "Planos", href: "/admin/plans", icon: Wallet },
    { name: "Pool Z-API", href: "/admin/z-api", icon: Server },
    { name: "Gerenciamento", href: "/admin/management", icon: Zap },
    { name: "Cobrança", href: "/admin/cobranca", icon: DollarSign },
    { name: "Wallet", href: "/admin/wallet", icon: Wallet },
  ].filter(Boolean);

  useEffect(() => {
    if (user?.cliente_id) {
      integracoesAPI.getCapabilities(user.cliente_id)
        .then(r => {
          setCapabilities(r.data?.capabilities || []);
          setTemIntegracao((r.data?.provedores || []).length > 0);
        })
        .catch(() => { setCapabilities([]); setTemIntegracao(false); });
    }
  }, [user?.cliente_id]);

  /**
   * Considera a rota ativa também em sub-rotas, para o item continuar destacado
   * em páginas de detalhe (ex.: /cobertura/segmento/:id mantém Cobertura ativo).
   * A barra no sufixo evita falso positivo entre rotas de prefixo comum.
   */
  const rotaAtiva = useCallback((href) => {
    if (!href) return false;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  }, [location.pathname]);

  useEffect(() => {
    const newOpen = {};
    navigation.forEach(item => {
      if (item.children && item.children.some(c => rotaAtiva(c.href))) {
        newOpen[item.name] = true;
      }
    });
    setOpenMenus(prev => ({ ...prev, ...newOpen }));
    // capabilities e as flags de instância entram nas dependências porque o
    // menu é montado a partir delas e chegam de forma assíncrona. Sem isso o
    // efeito rodava antes de Cobertura existir em navigation e o submenu
    // Marketing não abria — cada página monta seu próprio Layout, então
    // openMenus reseta a cada navegação e este efeito é o único que reabre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, capabilities, hasMetaInstance, hasInstagramInstance]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (fullName) => {
    if (fullName) {
      const initials = fullName
        .split(" ")
        .map((name) => name.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase();
      return initials;
    }
  };

  return (
      <div
        className={`${fullWidth ? 'h-[100dvh] flex flex-col overflow-hidden' : 'min-h-screen'} ${
          darkMode ? "content-bg-dark" : "bg-gray-100"
        }`}
      >
        {/* Mobile sidebar */}
        <div
          className={`fixed inset-0 flex z-50 md:hidden ${
            sidebarOpen ? "" : "hidden"
          }`}
        >
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div
            className={`relative flex-1 flex flex-col max-w-xs w-full ${
              darkMode ? "bg-gray-900" : "bg-white"
            }`}
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent
              navigation={navigation}
              location={location}
              onLogout={handleLogout}
              user={user}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              isAdmin={isAdmin}
              adminMenuOpen={adminMenuOpen}
              setAdminMenuOpen={setAdminMenuOpen}
              adminMenuItems={adminMenuItems}
              openMenus={openMenus}
              setOpenMenus={setOpenMenus}
            />
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ${sidebarCollapsed ? 'md:w-16' : 'md:w-64'}`}>
          <SidebarContent
            navigation={navigation}
            location={location}
            onLogout={handleLogout}
            user={user}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            isAdmin={isAdmin}
            adminMenuOpen={adminMenuOpen}
            setAdminMenuOpen={setAdminMenuOpen}
            adminMenuItems={adminMenuItems}
            openMenus={openMenus}
            setOpenMenus={setOpenMenus}
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
          />
        </div>

        {/* Main content */}
        <div className={`flex flex-col flex-1 transition-all duration-300 ${sidebarCollapsed ? 'md:pl-16' : 'md:pl-64'}`}>
          <div className={`header-navbar ${darkMode ? "sideMenu-bg-dark": 'bg-white'} fixed top-0 right-0 z-10 transition-all duration-300 ${sidebarCollapsed ? 'md:left-16' : 'md:left-64'} left-0`} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
            <div className="px-4 flex items-center justify-between h-full">
              <div className="header-left">
                <button
                  className={`md:hidden -ml-0.5 h-10 w-10 inline-flex items-center justify-center rounded-md ${
                    darkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-500 hover:text-gray-900"
                  } focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500`}
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>
              <div className="header-right">
                <NotificationBell darkMode={darkMode} />
                <div className="user-menu">
                  <div className="user-initials">{user ? getInitials(user.nome) : '?'}</div>
                </div>
              </div>
            </div>
          </div>
          <main className={`flex-1 overflow-hidden ${fullWidth ? 'mt-[92px]' : 'mt-24 overflow-auto'}`}>
            <div className={fullWidth ? 'h-full' : 'py-6'}>
              <div className={fullWidth ? 'h-full' : `${expanded ? 'max-w-[1600px]' : 'max-w-7xl'} mx-auto px-4 sm:px-10 md:px-4`}>
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>
  );
};

const SidebarContent = ({
  navigation,
  location,
  onLogout,
  user,
  darkMode,
  setDarkMode,
  isAdmin,
  adminMenuOpen,
  setAdminMenuOpen,
  adminMenuItems,
  openMenus,
  setOpenMenus,
  collapsed = false,
  onToggle,
}) => {
  const toggleMenu = (name) => !collapsed && setOpenMenus(prev => ({ ...prev, [name]: !prev[name] }));

  // Mantém o item destacado em sub-rotas (ex.: /cobertura/segmento/:id).
  // A barra no sufixo evita falso positivo entre rotas de prefixo comum.
  const rotaAtiva = (href) => {
    if (!href) return false;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const renderItem = (item) => {
    const Icon = item.icon;

    // Item com submenu
    if (item.children) {
      const isOpen = openMenus[item.name] && !collapsed;
      const isChildActive = item.children.some(c => rotaAtiva(c.href));
      return (
        <div key={item.name} className="space-y-1">
          <button
            onClick={() => toggleMenu(item.name)}
            title={collapsed ? item.name : undefined}
            className={`w-full sidemenu-item ${isChildActive ? "active" : ""} ${
              isChildActive
                ? darkMode ? "text-white" : "bg-green-100 text-green-900"
                : darkMode ? "text-gray-200 hover:bg-gray-800 hover:text-white" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            } group flex items-center ${collapsed ? 'justify-center px-0' : 'px-2'} py-2 text-sm font-medium rounded-md`}
          >
            <Icon className={`flex-shrink-0 h-6 w-6 ${collapsed ? '' : 'mr-3'}`} />
            {!collapsed && item.name}
            {!collapsed && (isOpen ? <ChevronDown className="ml-auto h-4 w-4" /> : <ChevronRight className="ml-auto h-4 w-4" />)}
          </button>
          {isOpen && (
            <div className="ml-6 space-y-1">
              {item.children.map(child => {
                const ChildIcon = child.icon;
                const isActive = rotaAtiva(child.href);
                return (
                  <Link key={child.name} to={child.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? darkMode ? "text-white bg-gray-700" : "bg-blue-50 text-blue-700"
                        : darkMode ? "text-gray-300 hover:bg-gray-700 hover:text-white" : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                    }`}
                  >
                    <ChildIcon className="mr-3 flex-shrink-0 h-5 w-5" />
                    {child.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Item simples
    const isActive = rotaAtiva(item.href);
    return (
      <Link key={item.name} to={item.href}
        title={collapsed ? item.name : undefined}
        className={`sidemenu-item ${isActive ? "active" : ""} ${
          isActive
            ? darkMode ? "text-white" : "bg-green-100 text-green-900"
            : darkMode ? "text-gray-200 hover:bg-gray-800 hover:text-white" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        } group flex items-center ${collapsed ? 'justify-center px-0' : 'px-2'} py-2 text-sm font-medium rounded-md`}
      >
        <Icon className={`flex-shrink-0 h-6 w-6 ${collapsed ? '' : 'mr-3'}`} />
        {!collapsed && item.name}
      </Link>
    );
  };

  return (
  <div
    className={`flex-1 flex flex-col min-h-0 ${
      darkMode ? "sideMenu-bg-dark" : "border-gray-200 bg-white"
    }`}
  >
    <div className="flex-1 flex flex-col sideMargin overflow-y-auto">
      <div className={`flex items-center sidebar-border h-[87px] ${collapsed ? 'justify-center px-2' : 'px-8'}`}>
        <img 
          src="/assets/logo_nomoz.png"
          width={177} 
          height={65} 
          className="logo"
          style={collapsed ? { maxWidth: '100%' } : undefined}
          alt="Nomoz Logo"
        />
      </div>
      <nav className={`mt-5 flex-1 ${collapsed ? 'px-1' : 'px-2'} space-y-1`}>
        {navigation.map(renderItem)}
        
        {/* Admin Menu */}
        {isAdmin && isAdmin() && (
          <div className="space-y-1">
            <button
              onClick={() => setAdminMenuOpen && setAdminMenuOpen(!adminMenuOpen)}
              title={collapsed ? "Painel Admin" : undefined}
              className={`w-full group flex items-center ${collapsed ? 'justify-center px-0' : 'px-2'} py-2 text-sm font-medium rounded-md ${
                location.pathname.startsWith('/admin')
                  ? darkMode
                    ? "text-white bg-gray-800"
                    : "bg-green-100 text-green-900"
                  : darkMode
                  ? "text-gray-200 hover:bg-gray-800 hover:text-white"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <Shield className={`flex-shrink-0 h-6 w-6 ${collapsed ? '' : 'mr-3'}`} />
              {!collapsed && 'Painel Admin'}
              {!collapsed && (adminMenuOpen ? (
                <ChevronDown className="ml-auto h-4 w-4" />
              ) : (
                <ChevronRight className="ml-auto h-4 w-4" />
              ))}
            </button>
            
            {adminMenuOpen && adminMenuItems && !collapsed && (
              <div className="ml-6 space-y-1">
                {adminMenuItems && adminMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = rotaAtiva(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? darkMode
                            ? "text-white bg-gray-700"
                            : "bg-blue-50 text-blue-700"
                          : darkMode
                          ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                          : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                      }`}
                    >
                      <Icon className="mr-3 flex-shrink-0 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>
    </div>
    <div
      className={`flex-shrink-0 flex flex-col border-t ${
        darkMode ? "border-gray-800" : "border-gray-200"
      } ${collapsed ? 'p-2' : 'p-4'}`}
    >
      {/* Toggle button */}
      {onToggle && (
        <button
          onClick={onToggle}
          className={`mb-3 p-2 rounded-md ${
            darkMode ? "hover:bg-gray-800 text-gray-300" : "hover:bg-gray-100 text-gray-500"
          } ${collapsed ? 'mx-auto' : ''}`}
          title={collapsed ? "Expandir menu" : "Retrair menu"}
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      )}
      <div className={`flex ${collapsed ? 'flex-col items-center space-y-2' : 'flex-col space-y-3'}`}>
        {!collapsed && <div>
          <p
            className={`text-sm font-medium ${
              darkMode ? "text-white" : "text-gray-700"
            }`}
          >
            {user?.nome || 'Usuário'}
          </p>
          <p
            className={`text-xs font-medium ${
              darkMode ? "text-gray-300" : "text-gray-500"
            }`}
          >
            {user?.email}
          </p>
        </div>}
        <div className={`flex ${collapsed ? 'flex-col space-y-2' : 'space-x-2'}`}>
          <button
            onClick={() => setDarkMode(!darkMode)}
            title={darkMode ? "Modo claro" : "Modo escuro"}
            className={`flex-shrink-0 p-1 rounded-full ${
              darkMode
                ? "bg-gray-800 text-gray-200 hover:text-white hover:bg-gray-700"
                : "bg-white text-gray-400 hover:text-gray-500"
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
          >
            {darkMode ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={onLogout}
            title="Sair"
            className={`flex-shrink-0 p-1 rounded-full ${
              darkMode
                ? "bg-gray-800 text-gray-200 hover:text-white hover:bg-gray-700"
                : "bg-white text-gray-400 hover:text-gray-500"
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
          >
            <LogOut className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  </div>
  );
};

export default Layout;
